/** Third-party pixel SDK loaders and event firing (Google Ads, Bing UET, LinkedIn). */

import { state } from "./state.js";
import { isMarketingAllowed } from "./consent.js";
import { DEFAULT_CURRENCY } from "./constants.js";
import type { EventPayload } from "./types.js";
import { recordPixelPayload } from "./types.js";
import { toMinorUnits } from "./currency-minor-units.js";

/**
 * PageView blocked for this platform via the per-rule platform selection
 * (event_mapping send:false)? Auto page events fired at SDK load (ttq.page,
 * pintrk page, GA4 config page_view) must respect this too; explicit events
 * are gated separately in event-transport's pixelBlocked().
 */
function isPageViewBlockedFor(platform: string): boolean {
  const platforms = state.config?.platforms as
    | Record<string, { blockedEvents?: string[] }>
    | undefined;
  const blocked = platforms?.[platform]?.blockedEvents;
  return Array.isArray(blocked) && blocked.indexOf("PageView") !== -1;
}

export { loadGoogleAdsPixel, fireGoogleAdsPixel } from "./google-pixel.js";

// --- Bing UET ---

/** Load Bing UET bat.js. */
export function loadBingPixel(tagId: string): void {
  if (state.initializedPixels["bing_" + tagId]) return;
  state.initializedPixels["bing_" + tagId] = true;

  if (window.uetq && typeof window.uetq.push === "function" && !Array.isArray(window.uetq)) {
    return;
  }

  // NonNullable, want `as typeof window.uetq` hield het optionele deel van het
  // type in stand: tsc zag daarna nog steeds `uetq | undefined` terwijl de regel
  // hem juist net vult. Runtime klopte het, de typecheck kon het alleen niet
  // volgen, en dit bestand lag buiten de typecheck dus niemand zag het.
  const uetq = (window.uetq || []) as NonNullable<typeof window.uetq>;
  window.uetq = uetq;
  // UET Consent Mode: without an explicit signal Microsoft stamps every
  // beacon asc=D (denied) and may restrict EEA data. Mirror our consent
  // state; the default must be queued BEFORE bat.js consumes the queue.
  const marketingGranted = isMarketingAllowed();
  uetq.push("consent", "default", {
    ad_storage: marketingGranted ? "granted" : "denied",
  });
  state.uetConsentGranted = marketingGranted;
  const scriptElement = document.createElement("script");
  scriptElement.async = true;
  scriptElement.src = "//bat.bing.com/bat.js";
  scriptElement.onload = () => {
    try {
      // bat.js does NOT consume window.uetq by itself: the official UET
      // snippet hands the queued commands to a `new UET(...)` instance and
      // replaces the queue with it. Without this step every push (pageview,
      // pid, conversions) landed in a dead array and no UET request ever
      // left the browser, while the event log showed the pixel as fired.
      if (!window.UET) return;
      const instance = new window.UET({
        ti: tagId,
        enableAutoSpaTracking: true,
        q: window.uetq,
      });
      window.uetq = instance as typeof window.uetq;
      window.uetq?.push("pageLoad");
    } catch {
      // UET init failed — the server channel still covers Microsoft
    }
  };
  document.head.appendChild(scriptElement);
}

/** Fire Bing/Microsoft UET event. */
export function fireBingPixel(payload: EventPayload): void {
  if (!window.uetq) return;
  if (!state.config || !state.config.platforms || !state.config.platforms.bing_ads) return;

  try {
    // Consent arrived after load (CMP accept): lift the UET consent state
    // once. fireBingPixel only runs when marketing consent is granted.
    if (!state.uetConsentGranted) {
      window.uetq.push("consent", "update", { ad_storage: "granted" });
      state.uetConsentGranted = true;
    }

    // Browser-side enhanced conversions: UET accepts pre-hashed SHA-256 pid
    // values. em_s carries Microsoft's strict normalization; ph_e is the
    // E.164 hash UET expects for phone.
    const pidEmail = state.userData.em_s || state.userData.em;
    const pidPhone = state.userData.ph_e;
    if (pidEmail || pidPhone) {
      const pid: Record<string, string> = {};
      if (pidEmail) pid.em = pidEmail;
      if (pidPhone) pid.ph = pidPhone;
      window.uetq.push("set", { pid });
    }

    const uetParams: Record<string, unknown> = {};
    if (payload.eventValue != null) {
      // revenue_value is THE revenue field for variable-revenue conversion
      // goals; event_value is only a goal-condition value. Both carry the
      // amount so either goal type picks it up; currency belongs to revenue.
      uetParams.revenue_value = payload.eventValue;
      uetParams.event_value = payload.eventValue;
      uetParams.currency = payload.currency || DEFAULT_CURRENCY;
    }
    window.uetq.push("event", payload.eventName!.toLowerCase(), uetParams);
    payload.pixelsFired.push("bing_ads");
    recordPixelPayload(payload, "bing_ads", { method: "uetq.push", args: ["event", payload.eventName!.toLowerCase(), uetParams] });
  } catch {
    // Bing UET pixel error — silently continue
  }
}

// --- GA4 (gtag) ---

/**
 * Load gtag for a GA4 property. gtag's automatic page_view on config powers
 * GA4 sessions and attribution; that is the whole point of the hybrid model
 * (server-only Measurement Protocol events report source "(not set)").
 */
export function loadGA4Pixel(measurementId: string): void {
  if (state.initializedPixels["ga4_" + measurementId]) return;
  state.initializedPixels["ga4_" + measurementId] = true;

  // PageView blocked for GA4 via the per-rule platform selection: suppress
  // the automatic page_view that config would otherwise fire
  const configParams: Record<string, unknown> | undefined = isPageViewBlockedFor("ga4")
    ? { send_page_view: false }
    : undefined;

  if (window.gtag) {
    state.selfDispatch = true;
    if (configParams) {
      window.gtag("config", measurementId, configParams);
    } else {
      window.gtag("config", measurementId);
    }
    state.selfDispatch = false;
    return;
  }

  const scriptElement = document.createElement("script");
  scriptElement.async = true;
  scriptElement.src = "https://www.googletagmanager.com/gtag/js?id=" + measurementId;
  document.head.appendChild(scriptElement);

  window.dataLayer = window.dataLayer || [];
  window.gtag = function () {
    window.dataLayer!.push(arguments);
  };
  state.selfDispatch = true;
  window.gtag("js", new Date());
  state.selfDispatch = false;
  state.selfDispatch = true;
  if (configParams) {
    window.gtag("config", measurementId, configParams);
  } else {
    window.gtag("config", measurementId);
  }
  state.selfDispatch = false;
}

/** Fire a GA4 gtag event for pixel-channel events (hybrid model). */
export function fireGA4Pixel(payload: EventPayload, eventId: string): void {
  if (!window.gtag) return;
  const ga4Config = state.config?.platforms?.ga4;
  if (!ga4Config) return;

  try {
    // PageView is covered by gtag's automatic page_view on config. gtag.js
    // loads async (still in flight at send time), so report it as fired when
    // the pixel was initialized: the dataLayer queue replays config on load.
    if (payload.eventName === "PageView") {
      if (ga4Config.measurementId && state.initializedPixels["ga4_" + ga4Config.measurementId]) {
        payload.pixelsFired.push("ga4");
        recordPixelPayload(payload, "ga4", { method: "gtag", args: ["config", ga4Config.measurementId], note: "page_view fires automatically on config" });
      }
      return;
    }

    // Only events the server marked as pixel-channel fire here (the server
    // skips those on MP)
    const pixelEvent = (ga4Config.pixelEvents || []).find(
      (entry) => entry.name === payload.eventName,
    );
    if (!pixelEvent) return;

    const params: Record<string, unknown> = {
      send_to: ga4Config.measurementId,
    };
    const cd = payload.customData ?? {};
    // transaction_id only where GA4 uses it (purchase dedup); prefer the
    // real order id so cross-source order dedup can pair
    if (pixelEvent.ga4Name === "purchase") {
      const orderId = typeof cd.order_id === "string" && cd.order_id ? cd.order_id : eventId;
      params.transaction_id = orderId;
    }
    if (pixelEvent.ga4Name === "search" && typeof cd.search_string === "string") {
      params.search_term = cd.search_string;
    }
    if (payload.eventValue != null) {
      params.value = payload.eventValue;
      params.currency = payload.currency || DEFAULT_CURRENCY;
    }

    state.selfDispatch = true;
    window.gtag("event", pixelEvent.ga4Name, params);
    state.selfDispatch = false;
    payload.pixelsFired.push("ga4");
    recordPixelPayload(payload, "ga4", { method: "gtag", args: ["event", pixelEvent.ga4Name, params] });
  } catch {
    // GA4 pixel error, silently continue
  }
}

// --- TikTok Pixel ---

/** Load the TikTok pixel (ttq) and fire the initial page view. */
export function loadTikTokPixel(pixelCode: string): void {
  if (state.initializedPixels["tt_" + pixelCode]) return;
  state.initializedPixels["tt_" + pixelCode] = true;

  if (window.ttq && typeof window.ttq.load === "function") {
    window.ttq.load(pixelCode);
    identifyTikTok();
    return;
  }

  // Compact version of TikTok's official loader IIFE: a command queue with
  // method stubs that the SDK replays once events.js has loaded. "load" and
  // "page" must be in this list: they are called on the stub right below, and
  // a missing stub throws and aborts the whole pixel-loading chain (the SDK
  // also auto-inits from the sdkid query param, so a queued load is safe).
  const methods = [
    "load", "page", "track", "identify", "instances", "debug", "on", "off",
    "once", "ready", "alias", "group", "enableCookie", "disableCookie",
    "holdConsent", "revokeConsent", "grantConsent",
  ];
  const queue: unknown[][] = [];
  const ttq = queue as unknown as Record<string, unknown>;
  for (const method of methods) {
    ttq[method] = function (...args: unknown[]) {
      queue.push([method, ...args]);
    };
  }
  window.TiktokAnalyticsObject = "ttq";
  window.ttq = ttq as Window["ttq"];

  const scriptElement = document.createElement("script");
  scriptElement.async = true;
  scriptElement.src =
    "https://analytics.tiktok.com/i18n/pixel/events.js?sdkid=" + pixelCode + "&lib=ttq";
  document.head.appendChild(scriptElement);

  (ttq.load as (c: string) => void)(pixelCode);
  identifyTikTok();
}

/**
 * TikTok advanced matching: identify() with the pre-hashed identifiers from
 * state.userData (the SDK accepts SHA-256 values). Called at pixel load and
 * again after PII capture so late form fills still lift the match rate.
 */
export function identifyTikTok(): void {
  if (!window.ttq) return;
  const identify = (window.ttq as { identify?: (data: Record<string, unknown>) => void }).identify;
  if (!identify) return;

  const data: Record<string, unknown> = {};
  if (state.userData.em) data.email = state.userData.em;
  if (state.userData.ph_e) data.phone_number = state.userData.ph_e;
  if (state.userData.external_id) data.external_id = state.userData.external_id;
  if (Object.keys(data).length === 0) return;

  try {
    identify(data);
  } catch {
    // TikTok identify error, silently continue
  }
}

/**
 * Push captured user data to every pixel SDK that supports late advanced
 * matching. Facebook has its own re-init path (reinitPixelUserData); this
 * covers the other platforms and is called from the same capture flows.
 */
export function refreshPlatformPixelUserData(): void {
  identifyTikTok();
  reinitSnapchatUserData();
  reinitPinterestUserData();
}

// --- Snapchat Pixel (snaptr) ---

/**
 * Canonical event -> Snap Pixel event name. MUST mirror the snapchat column
 * of EVENT_NAME_MAP (schemas/platforms.ts): Snap dedups pixel vs CAPI on
 * client_dedup_id/event_id ONLY when the event names match too.
 */
const SNAP_EVENT_NAMES: Record<string, string> = {
  PageView: "PAGE_VIEW",
  ViewContent: "VIEW_CONTENT",
  Lead: "SIGN_UP",
  AddToCart: "ADD_CART",
  InitiateCheckout: "START_CHECKOUT",
  Purchase: "PURCHASE",
  CompleteRegistration: "SIGN_UP",
  StartTrial: "START_TRIAL",
  Subscribe: "SUBSCRIBE",
  AddPaymentInfo: "ADD_BILLING",
  Contact: "SIGN_UP",
  Search: "SEARCH",
  AddToWishlist: "ADD_TO_WISHLIST",
  Schedule: "RESERVE",
  Donate: "PURCHASE",
  FindLocation: "CUSTOM_EVENT_4",
  SubmitApplication: "SIGN_UP",
  CustomizeProduct: "VIEW_CONTENT",
};

/** Load the Snapchat pixel (snaptr). No auto event: fireSnapchatPixel sends
 * every event, including PAGE_VIEW, with our dedup id. */
export function loadSnapchatPixel(pixelCode: string): void {
  if (state.initializedPixels["snap_" + pixelCode]) return;
  state.initializedPixels["snap_" + pixelCode] = true;

  if (!window.snaptr) {
    // Official loader: command queue replayed once scevent.min.js lands
    const snap = function (...args: unknown[]) {
      if (snap.handleRequest) {
        (snap.handleRequest as (...a: unknown[]) => void)(...args);
      } else {
        snap.queue!.push(args);
      }
    } as NonNullable<Window["snaptr"]>;
    snap.queue = [];
    window.snaptr = snap;

    const scriptElement = document.createElement("script");
    scriptElement.async = true;
    scriptElement.src = "https://sc-static.net/scevent.min.js";
    document.head.appendChild(scriptElement);
  }

  window.snaptr("init", pixelCode, buildSnapInitUserData());
}

/** Snap init user object: pre-hashed email/phone lift the pixel match rate
 * (user_hashed_* fields take already-hashed values). */
function buildSnapInitUserData(): Record<string, unknown> {
  const data: Record<string, unknown> = {};
  if (state.userData.em) data.user_hashed_email = state.userData.em;
  if (state.userData.ph) data.user_hashed_phone_number = state.userData.ph;
  return data;
}

/** Re-init the Snap pixel with user data captured after load. */
export function reinitSnapchatUserData(): void {
  if (!window.snaptr) return;
  const pixelCode = state.config?.platforms?.snapchat?.pixelCode;
  if (!pixelCode || !state.initializedPixels["snap_" + pixelCode]) return;
  const data = buildSnapInitUserData();
  if (Object.keys(data).length === 0) return;
  try {
    window.snaptr("init", pixelCode, data);
  } catch {
    // Snapchat re-init error, silently continue
  }
}

/** Fire a Snapchat pixel event with client_dedup_id for CAPI dedup (48h). */
export function fireSnapchatPixel(payload: EventPayload, eventId: string): void {
  if (!window.snaptr) return;
  if (!state.config || !state.config.platforms || !state.config.platforms.snapchat) return;

  try {
    // Server-side event_mapping renames must reach the pixel too: Snap
    // dedups on client_dedup_id ONLY when the event names match as well
    const snapRenameMap = state.config.platforms.snapchat.renameMap;
    const snapRenamed = snapRenameMap && snapRenameMap[payload.eventName!];
    const snapEventName = snapRenamed || SNAP_EVENT_NAMES[payload.eventName!] || payload.eventName!;
    const params: Record<string, unknown> = {
      // Pairs with the CAPI v3 top-level event_id; without an explicit value
      // the SDK generates a browser-only id that can never match our server
      client_dedup_id: eventId,
    };
    if (payload.eventValue != null) {
      params.price = payload.eventValue;
      params.currency = payload.currency || DEFAULT_CURRENCY;
    }
    // E-commerce/search context mirrors the CAPI custom_data
    const cd = payload.customData ?? {};
    if (Array.isArray(cd.content_ids) && cd.content_ids.length > 0) params.item_ids = cd.content_ids;
    if (typeof cd.content_category === "string") params.item_category = cd.content_category;
    if (typeof cd.num_items === "number") params.number_items = cd.num_items;
    if (typeof cd.order_id === "string") params.transaction_id = cd.order_id;
    if (typeof cd.search_string === "string") params.search_string = cd.search_string;

    window.snaptr("track", snapEventName, params);
    payload.pixelsFired.push("snapchat");
    recordPixelPayload(payload, "snapchat", { method: "snaptr", args: ["track", snapEventName, params] });
  } catch {
    // Snapchat pixel error, silently continue
  }
}

// --- Pinterest Tag (pintrk) ---

/**
 * Canonical event -> Pinterest tag event name (documented tag-side names,
 * lowercase without underscores). Semantically mirrors the pinterest column
 * of EVENT_NAME_MAP; Pinterest normalizes tag names to the API's snake_case
 * for event_id-based dedup (24h window, first event wins).
 */
const PINTEREST_EVENT_NAMES: Record<string, string> = {
  PageView: "pagevisit",
  ViewContent: "pagevisit",
  Lead: "lead",
  AddToCart: "addtocart",
  InitiateCheckout: "initiate_checkout",
  Purchase: "checkout",
  CompleteRegistration: "signup",
  StartTrial: "start_trial",
  Subscribe: "subscribe",
  AddPaymentInfo: "add_payment_info",
  Contact: "contact",
  Search: "search",
  AddToWishlist: "add_to_wishlist",
  Schedule: "schedule",
  Donate: "checkout",
  FindLocation: "find_location",
  SubmitApplication: "submit_application",
  CustomizeProduct: "customize_product",
};

/** Load the Pinterest tag; pintrk('page') registers the base page load for
 * audience building (the pagevisit conversion event fires separately). */
export function loadPinterestPixel(tagId: string): void {
  if (state.initializedPixels["pin_" + tagId]) return;
  state.initializedPixels["pin_" + tagId] = true;

  if (!window.pintrk) {
    const pin = function (...args: unknown[]) {
      window.pintrk!.queue!.push(Array.prototype.slice.call(args));
    } as NonNullable<Window["pintrk"]>;
    pin.queue = [];
    pin.version = "3.0";
    window.pintrk = pin;

    const scriptElement = document.createElement("script");
    scriptElement.async = true;
    scriptElement.src = "https://s.pinimg.com/ct/core.js";
    document.head.appendChild(scriptElement);
  }

  window.pintrk("load", tagId, buildPinterestEnhancedMatch());
  if (!isPageViewBlockedFor("pinterest")) {
    window.pintrk("page");
  }
}

/** Enhanced match object for pintrk load: the pre-hashed email lifts tag-side
 * attribution (cookieless Safari) and tag-vs-API user joining. */
function buildPinterestEnhancedMatch(): Record<string, unknown> {
  const data: Record<string, unknown> = {};
  if (state.userData.em) data.em = state.userData.em;
  return data;
}

/** Re-load the Pinterest tag with enhanced match after late PII capture. */
export function reinitPinterestUserData(): void {
  if (!window.pintrk) return;
  const tagId = state.config?.platforms?.pinterest?.tagId;
  if (!tagId || !state.initializedPixels["pin_" + tagId]) return;
  const data = buildPinterestEnhancedMatch();
  if (Object.keys(data).length === 0) return;
  try {
    window.pintrk("load", tagId, data);
  } catch {
    // Pinterest re-load error, silently continue
  }
}

/** Fire a Pinterest tag event with event_id for Conversions API dedup. */
export function firePinterestPixel(payload: EventPayload, eventId: string): void {
  if (!window.pintrk) return;
  if (!state.config || !state.config.platforms || !state.config.platforms.pinterest) return;

  try {
    // Server-side event_mapping renames must reach the tag too: Pinterest
    // dedups on event_id ONLY when the (normalized) event names match
    const pinRenameMap = state.config.platforms.pinterest.renameMap;
    const pinRenamed = pinRenameMap && pinRenameMap[payload.eventName!];
    const pinEventName = pinRenamed || PINTEREST_EVENT_NAMES[payload.eventName!] || "custom";
    const params: Record<string, unknown> = {
      event_id: eventId,
    };
    if (payload.eventValue != null) {
      params.value = payload.eventValue;
      params.currency = payload.currency || DEFAULT_CURRENCY;
    }
    // E-commerce/search context mirrors the CAPI custom_data
    const cd = payload.customData ?? {};
    if (typeof cd.order_id === "string") params.order_id = cd.order_id;
    if (typeof cd.num_items === "number") params.order_quantity = cd.num_items;
    if (typeof cd.search_string === "string") params.search_query = cd.search_string;
    if (Array.isArray(cd.content_ids) && cd.content_ids.length > 0) {
      params.line_items = (cd.content_ids as unknown[]).map((id) => ({ product_id: String(id) }));
    }

    window.pintrk("track", pinEventName, params);
    payload.pixelsFired.push("pinterest");
    recordPixelPayload(payload, "pinterest", { method: "pintrk", args: ["track", pinEventName, params] });
  } catch {
    // Pinterest tag error, silently continue
  }
}

// --- Reddit Pixel (rdt) ---

/**
 * Canonical event -> Reddit pixel event name. Mirrors the semantics of the
 * reddit column of EVENT_NAME_MAP / REDDIT_STANDARD_EVENTS in the adapter;
 * anything else goes out as Custom with the canonical name, matching the
 * server's CUSTOM + custom_event_name so conversionId dedup still pairs.
 */
const REDDIT_EVENT_NAMES: Record<string, string> = {
  PageView: "PageVisit",
  ViewContent: "ViewContent",
  Lead: "Lead",
  AddToCart: "AddToCart",
  Purchase: "Purchase",
  CompleteRegistration: "SignUp",
  Search: "Search",
  // Reddit event names are case-sensitive: "AddToWishlist" (lowercase l)
  AddToWishlist: "AddToWishlist",
  // Folds onto standard events, mirroring the corrected reddit column
  // (invalid pass-through names were dead CUSTOM signals before)
  StartTrial: "SignUp",
  Subscribe: "Purchase",
  Contact: "Lead",
  Schedule: "Lead",
  Donate: "Purchase",
  SubmitApplication: "Lead",
};

/**
 * Rename values from the event mapping arrive in the API's UPPER_SNAKE enum
 * (the server sends them as tracking_type); the rdt tag uses PascalCase
 * names for those same standard events. Mirrors REDDIT_STANDARD_EVENTS in
 * RedditAdapter.ts; renamed values outside this set go out as Custom +
 * customEventName on BOTH channels so conversionId dedup still pairs.
 */
const REDDIT_API_TO_PIXEL: Record<string, string> = {
  PAGE_VISIT: "PageVisit",
  VIEW_CONTENT: "ViewContent",
  SEARCH: "Search",
  ADD_TO_CART: "AddToCart",
  ADD_TO_WISHLIST: "AddToWishlist",
  PURCHASE: "Purchase",
  LEAD: "Lead",
  SIGN_UP: "SignUp",
};

/** Load the Reddit pixel (rdt). No auto event: fireRedditPixel sends
 * PageVisit with our conversionId so dedup against CAPI works. */
export function loadRedditPixel(pixelId: string): void {
  if (state.initializedPixels["rdt_" + pixelId]) return;
  state.initializedPixels["rdt_" + pixelId] = true;

  if (!window.rdt) {
    const rdt = function (...args: unknown[]) {
      if (rdt.sendEvent) {
        (rdt.sendEvent as (...a: unknown[]) => void)(...args);
      } else {
        rdt.callQueue!.push(args);
      }
    } as NonNullable<Window["rdt"]>;
    rdt.callQueue = [];
    window.rdt = rdt;

    const scriptElement = document.createElement("script");
    scriptElement.async = true;
    scriptElement.src = "https://www.redditstatic.com/ads/pixel.js";
    document.head.appendChild(scriptElement);
  }

  // useDecimalCurrencyValues is essential: without it the pixel reads our
  // decimal values as CENTS (a 99.95 purchase would count as 0.9995).
  // Advanced matching can only ride along at init (first event of the page).
  const initOptions: Record<string, unknown> = { useDecimalCurrencyValues: true };
  // em_r before em, exactly as RedditAdapter does it server-side. Reddit
  // canonicalises an address by dropping the dots and the "+suffix" from the
  // local part before hashing, which the base em hash deliberately keeps.
  // Sending the base hash here would not just miss the match, it would send a
  // DIFFERENT key than the server does for the same person, so the two channels
  // would disagree about who this is.
  const redditEmail = state.userData.em_r ?? state.userData.em;
  if (redditEmail) initOptions.email = redditEmail;
  if (state.userData.external_id) initOptions.externalId = state.userData.external_id;
  window.rdt("init", pixelId, initOptions);
}

/** Fire a Reddit pixel event with conversionId for CAPI dedup (hourly runs,
 * 2-day send window). */
export function fireRedditPixel(payload: EventPayload, eventId: string): void {
  if (!window.rdt) return;
  if (!state.config || !state.config.platforms || !state.config.platforms.reddit) return;

  try {
    const params: Record<string, unknown> = {
      // Pairs with metadata.conversion_id on the Conversions API side
      conversionId: eventId,
    };
    if (payload.eventValue != null) {
      params.value = payload.eventValue;
      params.currency = payload.currency || DEFAULT_CURRENCY;
    }

    // Server-side event_mapping renames must reach the pixel too, with the
    // server's standard-vs-custom split mirrored exactly
    const redditRenameMap = state.config.platforms.reddit.renameMap;
    const redditRenamed = redditRenameMap && redditRenameMap[payload.eventName!];
    let redditEventName: string | undefined;
    let redditCustomName: string | null = null;
    if (redditRenamed) {
      const standardPixelName = REDDIT_API_TO_PIXEL[redditRenamed];
      if (standardPixelName) {
        redditEventName = standardPixelName;
      } else {
        redditCustomName = redditRenamed;
      }
    } else {
      redditEventName = REDDIT_EVENT_NAMES[payload.eventName!];
      if (!redditEventName) redditCustomName = payload.eventName!;
    }

    if (redditEventName) {
      window.rdt("track", redditEventName, params);
      recordPixelPayload(payload, "reddit", { method: "rdt", args: ["track", redditEventName, params] });
    } else {
      params.customEventName = redditCustomName!;
      window.rdt("track", "Custom", params);
      recordPixelPayload(payload, "reddit", { method: "rdt", args: ["track", "Custom", params] });
    }
    payload.pixelsFired.push("reddit");
  } catch {
    // Reddit pixel error, silently continue
  }
}

// --- OpenAI Ads Pixel (oaiq) ---

/**
 * Canonical event -> OpenAI wire name; MUST equal the openai_ads column of
 * EVENT_NAME_MAP verbatim, because OpenAI dedups on Pixel ID + event name +
 * event_id and the CAPI side sends exactly these strings as `type` (or as
 * custom_event_name for the non-taxonomy names).
 */
const OPENAI_EVENT_NAMES: Record<string, string> = {
  PageView: "page_viewed",
  ViewContent: "contents_viewed",
  Lead: "lead_created",
  AddToCart: "items_added",
  InitiateCheckout: "checkout_started",
  Purchase: "order_created",
  CompleteRegistration: "registration_completed",
  StartTrial: "trial_started",
  Subscribe: "subscription_created",
  Schedule: "appointment_scheduled",
};

/**
 * data.type OpenAI prescribes per taxonomy type; mirror of OPENAI_DATA_TYPE_OF
 * in OpenAIAdsAdapter.ts. A wire name outside this table fires as "custom"
 * with the name in custom_event_name, matching the CAPI custom path.
 */
const OPENAI_PIXEL_DATA_TYPE: Record<string, string> = {
  appointment_scheduled: "customer_action",
  lead_created: "customer_action",
  registration_completed: "customer_action",
  checkout_started: "contents",
  contents_viewed: "contents",
  items_added: "contents",
  order_created: "contents",
  page_viewed: "contents",
  subscription_created: "plan_enrollment",
  trial_started: "plan_enrollment",
};

/** Load the OpenAI Ads pixel (oaiq). No auto event: fireOpenAIAdsPixel sends
 * everything with our event_id so dedup against the Conversions API pairs. */
export function loadOpenAIAdsPixel(pixelId: string): void {
  if (state.initializedPixels["oaiq_" + pixelId]) return;
  state.initializedPixels["oaiq_" + pixelId] = true;

  if (!window.oaiq) {
    const oaiq = function (...args: unknown[]) {
      oaiq.q!.push(args);
    } as NonNullable<Window["oaiq"]>;
    oaiq.q = [];
    window.oaiq = oaiq;

    const scriptElement = document.createElement("script");
    scriptElement.async = true;
    scriptElement.src = "https://bzrcdn.openai.com/sdk/oaiq.min.js";
    document.head.appendChild(scriptElement);
  }

  window.oaiq("init", { pixelId });
  // Mirror our consent state into the SDK's own consent switch. The load
  // itself is already consent-gated (safePixelLoad runs after the banner),
  // so this is belt-and-braces for the SDK's internal behavior.
  window.oaiq("consent", isMarketingAllowed());
}

/** Fire an OpenAI Ads pixel event with event_id for CAPI dedup. */
export function fireOpenAIAdsPixel(payload: EventPayload, eventId: string): void {
  if (!window.oaiq) return;
  if (!state.config || !state.config.platforms || !state.config.platforms.openai_ads) return;

  try {
    // Server-side event_mapping renames must reach the pixel too, with the
    // server's taxonomy-vs-custom split mirrored exactly
    const renameMap = state.config.platforms.openai_ads.renameMap;
    const renamed = renameMap && renameMap[payload.eventName!];
    let wireName: string;
    if (renamed) {
      wireName = renamed;
    } else {
      wireName = OPENAI_EVENT_NAMES[payload.eventName!] || payload.eventName!;
    }

    const dataType = OPENAI_PIXEL_DATA_TYPE[wireName];
    const eventData: Record<string, unknown> = {};
    if (dataType !== undefined) {
      eventData.type = dataType;
    } else {
      eventData.type = "custom";
    }
    if (payload.eventValue != null) {
      const currency = payload.currency || DEFAULT_CURRENCY;
      // INTEGER minor units, same shared table as the server adapter: a
      // decimal amount here would be wrong by 100x and dedup would still
      // "work", making the mistake invisible.
      eventData.amount = toMinorUnits(Number(payload.eventValue), currency);
      eventData.currency = currency;
    }

    // Pairs with the CAPI event `id`
    const options: Record<string, unknown> = { event_id: eventId };
    let measureName = wireName;
    if (dataType === undefined) {
      measureName = "custom";
      options.custom_event_name = wireName;
    }

    window.oaiq("measure", measureName, eventData, options);
    payload.pixelsFired.push("openai_ads");
    recordPixelPayload(payload, "openai_ads", { method: "oaiq", args: ["measure", measureName, eventData, options] });
  } catch {
    // OpenAI pixel error, silently continue
  }
}

/**
 * Canonical -> TikTok pixel event names for events whose canonical name is
 * NOT a TikTok standard event. MUST mirror the tiktok column of
 * EVENT_NAME_MAP (schemas/platforms.ts): dedup pairs only on identical
 * event name + event_id across pixel and Events API.
 */
const TIKTOK_EVENT_NAMES: Record<string, string> = {
  // Lead needs no entry since TikTok's 2025-05-01 rename (canonical == wire);
  // frozen pre-rename destinations get SubmitForm via the renameMap instead.
  Donate: "Purchase",
};

/** Fire a TikTok pixel event with event_id for server-side dedup. */
export function fireTikTokPixel(payload: EventPayload, eventId: string): void {
  if (!window.ttq) return;
  if (!state.config || !state.config.platforms || !state.config.platforms.tiktok) return;

  try {
    // Pageviews go through ttq.page WITH our event_id so they dedup against
    // the server's Pageview (a bare page() at load could never pair).
    // event_id goes in the FIRST argument: page() takes its options there,
    // unlike track() where options are the third argument. With the id in a
    // second argument TikTok silently dropped it (Events Manager dedup
    // coverage showed 0% browser event_id on Page view, 2026-07-22); it is
    // passed in both positions so a signature change cannot regress this.
    if (payload.eventName === "PageView") {
      const page = (window.ttq as { page?: (p: Record<string, unknown>, o: Record<string, unknown>) => void }).page;
      if (page) {
        page({ event_id: eventId }, { event_id: eventId });
        payload.pixelsFired.push("tiktok");
        recordPixelPayload(payload, "tiktok", { method: "ttq.page", args: [{ event_id: eventId }, { event_id: eventId }] });
      }
      return;
    }

    const props: Record<string, unknown> = {};
    if (payload.eventValue != null) {
      props.value = payload.eventValue;
      props.currency = payload.currency || DEFAULT_CURRENCY;
    }

    // TikTok dedups on identical event name + event_id: a server-side rename
    // (platformEventName in the event mapping) must be mirrored here, or the
    // pixel and Events API events can never pair. renameMap carries the
    // server's resolved outgoing names; the static map is the fallback.
    const tiktokConfig = state.config && state.config.platforms && state.config.platforms.tiktok;
    const renameMap = tiktokConfig && tiktokConfig.renameMap;
    const renamed = renameMap && renameMap[payload.eventName!];
    const ttEventName = renamed || TIKTOK_EVENT_NAMES[payload.eventName!] || payload.eventName!;
    (window.ttq as { track: (e: string, p: Record<string, unknown>, o: Record<string, unknown>) => void })
      .track(ttEventName, props, { event_id: eventId });
    payload.pixelsFired.push("tiktok");
    recordPixelPayload(payload, "tiktok", { method: "ttq.track", args: [ttEventName, props, { event_id: eventId }] });
  } catch {
    // TikTok pixel error, silently continue
  }
}

// --- LinkedIn Insight ---

/** Load LinkedIn Insight tag. */
export function loadLinkedInPixel(partnerId: string): void {
  if (state.initializedPixels["li_" + partnerId]) return;
  state.initializedPixels["li_" + partnerId] = true;

  if (window.lintrk) return;

  window._linkedin_partner_id = partnerId;
  window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
  window._linkedin_data_partner_ids.push(partnerId);

  // LinkedIn's own queue stub. Without it a conversion fired before
  // insight.min.js has loaded (every page-view rule fires right after config
  // load) found no window.lintrk and was dropped: 0 of 32 sign-ups on
  // Rechtswijs reached the Insight Tag. The real script drains the queue.
  const queue: Array<[string, Record<string, unknown>]> = [];
  const stub = (action: string, data: Record<string, unknown>) => { queue.push([action, data]); };
  window.lintrk = Object.assign(stub, { q: queue });

  const scriptElement = document.createElement("script");
  scriptElement.async = true;
  scriptElement.src = "https://snap.licdn.com/li.lms-analytics/insight.min.js";
  document.head.appendChild(scriptElement);
}

/** Fire LinkedIn Insight conversion event. */
export function fireLinkedInPixel(payload: EventPayload, eventId: string): void {
  if (!window.lintrk) return;
  if (!state.config || !state.config.platforms || !state.config.platforms.linkedin) return;

  // Per-event conversion id from the mapping wins; the flat conversionId is
  // the legacy single-conversion fallback
  const liConfig = state.config.platforms.linkedin;
  const mappedId = liConfig.conversionMap ? liConfig.conversionMap[payload.eventName!] : undefined;
  const lnConversionId = mappedId || liConfig.conversionId;
  if (!lnConversionId) return;

  try {
    // event_id pairs with the CAPI eventId: LinkedIn dedups Insight Tag vs
    // CAPI only on an identical event id (the tag event then wins)
    window.lintrk("track", { conversion_id: Number(lnConversionId), event_id: eventId });
    payload.pixelsFired.push("linkedin");
    recordPixelPayload(payload, "linkedin", { method: "lintrk", args: ["track", { conversion_id: Number(lnConversionId), event_id: eventId }] });
  } catch {
    // LinkedIn pixel error — silently continue
  }
}
